import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { v4 as uuid } from "uuid";
import { Container, ReportContainer } from "../styles";
import EmployeeCostHeader from "./EmployeeCostHeader/EmployeeCostHeader";
import EmployeeCostSchema from "./EmployeeCostSchema/EmployeeCostSchema";
import EmployeePaySchema from "./EmployeePaySchema/EmployeePaySchema";
import EmployeeCostActions from "../EmployeeCostActions/EmployeeCostActions";
import { ProformaEmployeeCostReportSectionRef } from "../../../models/proforma-employee-cost-report/proforma-employee-cost-report-section-ref";
import { ProformaEmployeeCostReportDTO } from "../../../models/proforma-employee-cost-report/proforma-employee-cost-report-dto";
import { LegislationEmployeeCostSectionApiService } from "../../../services/legislation-employee-cost-section/legislation-employee-cost-section-api.service";

import { LegislationSocialSecurityCostApiService } from "../../../services/legislation-social-security-cost/legislation-social-security-cost-api.service";

import { ProformaEmployeeCostReportApiService } from "../../../services/proforma-employee-cost-report/proforma-employee-cost-report-api.service";
import { CompanyLegalEntityDTO, CompanyLegalEntityRef } from "../../../models/company-accounts/company-legal-entities";
import { exportReport, generatePDFReport } from "../utils";
import ExportedProformaEmployeeCostReport from "../ExportedProformaEmployeeCostReport/ExportedProformaEmployeeCostReport";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import { UserRef } from "../../../models/user";
import { DocumentRef, DocumentSTO } from "../../../models/documents";
import { DOCUMENTS_STATUS_TYPES, DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../../utils/constants/constants";
import { DocumentTypeRef } from "../../../models/document-types";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { Period, PeriodType } from "../../../models/documents/period";
import { DocumentCategoryRef } from "../../../models/document-categories";
import PreviewProformaEmployeeCostReport from "../PreviewProformaEmployeeCostReport";
import { saveDocument } from "../../../services/documents";
import { handleUploadNewFiles } from "../../Document/Document/utils";
import { SendProformaEmployeeCostReportApiService } from "../../../services/send-proforma-employee-cost-report/send-proforma-employee-cost-report-api.service";
import { toastifyResponse } from "../../../utils/toastify-response/toastify-response";
import { EMPLOYEE_COST_SECTION_TYPES, SELECTED_PERIOD } from "../../../utils/constants/employee-cost-report-constants";
import { LegislationEmployeeCostSectionFilter } from "../../../models/legislation-employee-cost-section/legislation-employee-cost-section-filter";
import CustomConfirmationChangesDialog from "../../CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { Alert, Dialog, DialogContent, Grid, Typography } from "@mui/material";
import { LegislationSocialSecurityCostsFilter } from "../../../models/legislation-social-security-cost/legislation-social-security-costs-filter";
import DialogActions from "../../DialogActions/DialogActions";
import { LegislationSocialSecurityCostDTO } from "../../../models/legislation-social-security-cost/legislation-social-security-cost-dto";
import { isAfter } from "date-fns";
import DialogTitle from "../../DialogTitle/DialogTitle";
import { intlFormatDateShort } from "../../../utils/time-utils";
import LegislationSectionConfiguration from "../LegislationSectionConfiguration/LegislationSectionConfiguration";
import { LegislationEmployeeCostConfigurationTypeDTO } from "../../../models/legislation-employee-cost-configuration-type/legislation-employee-cost-configuration-type-dto";
import { useTranslation } from "react-i18next";
import { Props, IProformaEmployeeCostReportContext, State } from "./interfaces";

export const ProformaEmployeeCostReportContext = createContext<IProformaEmployeeCostReportContext>({
  originalReport: null,
  report: null,
  viewMode: false,
  currentSocialSecurityConfig: null,
  applicableSocialSecurityConfigs: [],
  reportUpdated: false,
  setReportUpdated: () => {},
  grossSalary: 0,
});

const ProformaEmployeeCostReport = ({
  effect = "add",
  item,
  legalEntity,
  handleDiscard,
  handleNotifyParent,
  handleCloseParent,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_proforma_employee_cost_report";
  const theme = useTheme();
  const DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT: Partial<ProformaEmployeeCostReportDTO> = useMemo(
    () => ({
      id: uuid(),
      companyAccount: legalEntity?.companyAccount ?? null,
      legalEntity: legalEntity ?? null,
      currency: legalEntity?.defaultCurrency ?? null,
      // employee: "",
      // project: "",
      description: "",
      applicableFrom: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        day: new Date().getDate(),
      },
      // applicableTo: null,
      sections: [],
      isAcceptedByClient: null,
      selectedPeriod: SELECTED_PERIOD.yearly,
      legislationEmployeeCostConfigurationType: null,
    }),
    [legalEntity]
  );

  const [state, setState] = useState<State>({
    report: item ?? (DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT as ProformaEmployeeCostReportDTO),
    loading: {
      saving: false,
      legislationSections: false,
      socialSecuritySections: false,
      exporting: false,
      sending: false,
    },
    legislationSections: [],
    error: "",
    socialSecurityConfigs: [],
    applicableSocialSecurityConfigs: [],
    currentSocialSecurityConfig: null,
    sSecurityConfigWarningOpen: false,
    file: null,
    users: [],
    externalUsers: [],
    newLegalEntity: null,
    reportUpdated: false,
    newConfigurationType: undefined,
  });

  //#region handle update section
  const handleUpdateSection = useCallback((updatedSection: ProformaEmployeeCostReportSectionRef) => {
    setState((state) => ({
      ...state,
      report: {
        ...state.report,
        sections: [
          ...state.report.sections.filter((section) => section.type?.id !== updatedSection.type?.id),
          updatedSection,
        ],
      },
    }));
  }, []);
  //#endregion

  //#region handle update general info
  const handleUpdateGeneralInfo = useCallback(
    (
      //This is to update fields other than sections
      field: keyof ProformaEmployeeCostReportDTO,
      value: ProformaEmployeeCostReportDTO[keyof ProformaEmployeeCostReportDTO]
    ) => {
      setState((state) => ({
        ...state,
        report: {
          ...state.report,
          [field]: value,
        },
      }));
    },
    []
  );
  //#endregion

  //#region handle update legal entity
  const handleUpdateNewLegalEntity = useCallback((legalEntity: CompanyLegalEntityDTO | null) => {
    setState((state) => ({
      ...state,
      newLegalEntity: legalEntity,
    }));
  }, []);
  //#endregion

  //#region handle update configuration type
  const handleUpdateConfigurationType = useCallback(
    (configurationType: LegislationEmployeeCostConfigurationTypeDTO | null) => {
      setState((state) => ({
        ...state,
        newConfigurationType: configurationType,
      }));
    },
    []
  );
  //#endregion

  // #region Load legislation sections
  const loadLegislationSections = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: {
        ...state.loading,
        legislationSections: true,
      },
    }));
    try {
      const response = await new LegislationEmployeeCostSectionApiService().loadItems(
        {
          legislationIds: [state.report.legalEntity?.legislation?.id],
          legislationEmployeeCostConfigurationTypeIds: state.report.legislationEmployeeCostConfigurationType
            ? [state.report.legislationEmployeeCostConfigurationType.id]
            : [],
          joinCommonAndConfigured: state.report.legislationEmployeeCostConfigurationType ? true : false,
        } as LegislationEmployeeCostSectionFilter,
        undefined,
        [],
        {
          pageNumber: 0,
          pageSize: 1000,
        },
        undefined,
        undefined,
        undefined
      );

      if (!response.error) {
        setState((state) => ({
          ...state,
          error: "",
          legislationSections: response.items,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: {
          ...state.loading,
          legislationSections: false,
        },
      }));
    }
  }, [state.report.legalEntity?.legislation, state.report.legislationEmployeeCostConfigurationType]);

  //#endregion

  //#region Load legislation social security costs and pick the right one
  const getGrossSalaryAmount = useCallback(() => {
    return (
      state.report?.sections.find((section) => section.type?.type?.id === EMPLOYEE_COST_SECTION_TYPES.Gross_salary.id)
        ?.amount ?? 0
    );
  }, [state.report?.sections]);

  //#region pick correct social security costs
  const pickRightSocialSecurityCosts = useCallback(
    (ssCosts: LegislationSocialSecurityCostDTO[]) => {
      //first check the applicable from period
      //second check the from and to gross salary
      const correspondingByDate = ssCosts
        .filter(
          (legislationSSCost: LegislationSocialSecurityCostDTO) =>
            legislationSSCost.legislation?.id === state.report.legalEntity?.legislation?.id &&
            legislationSSCost.applicableFrom &&
            state.report.applicableFrom &&
            (isAfter(
              CustomDate.ToDate(state.report.applicableFrom)!,
              CustomDate.ToDate(legislationSSCost.applicableFrom)!
            ) ||
              CustomDate.ToDate(state.report.applicableFrom)!.getTime() ===
                CustomDate.ToDate(legislationSSCost.applicableFrom)!.getTime())
        )
        .sort(
          (a, b) => CustomDate.ToDate(b.applicableFrom)?.getTime()! - CustomDate.ToDate(a.applicableFrom)?.getTime()!
        );

      const allCorrespondingDateAndGSInterval = correspondingByDate.filter(
        (legislationSSCost: LegislationSocialSecurityCostDTO) =>
          getGrossSalaryAmount() >= legislationSSCost.fromGrossSalary &&
          getGrossSalaryAmount() <= legislationSSCost.toGrossSalary
      );

      const correspondingDateAndGSInterval = allCorrespondingDateAndGSInterval[0];

      const correspondingByDateAndGSAndConfigType = allCorrespondingDateAndGSInterval.filter(
        (legislationSSCost: LegislationSocialSecurityCostDTO) =>
          legislationSSCost.legislationEmployeeCostConfigurationType?.id ===
            state.report.legislationEmployeeCostConfigurationType?.id ||
          !legislationSSCost.legislationEmployeeCostConfigurationType
      )[0];

      const correspondingSSConfig = state.report.legislationEmployeeCostConfigurationType
        ? correspondingByDateAndGSAndConfigType ?? correspondingDateAndGSInterval
        : correspondingDateAndGSInterval;

      setState((state) => ({
        ...state,
        applicableSocialSecurityConfigs: correspondingByDate.sort((a, b) => a.fromGrossSalary - b.fromGrossSalary),
        currentSocialSecurityConfig: correspondingSSConfig ?? null,
        // sSecurityConfigWarningOpen: !correspondingSSConfig,
      }));
    },
    [
      state.report.applicableFrom,
      getGrossSalaryAmount,
      state.report.legalEntity?.legislation?.id,
      state.report.legislationEmployeeCostConfigurationType,
    ]
  );
  //#endregion

  //#region load social secuity
  const loadSocialSecuritySectionCosts = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: {
        ...state.loading,
        socialSecuritySections: true,
      },
    }));
    try {
      const response = await new LegislationSocialSecurityCostApiService().loadItems({
        legislationIds: [state.report.legalEntity?.legislation?.id],
      } as LegislationSocialSecurityCostsFilter);
      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: "",
          socialSecurityConfigs: response.items[0]?.legislationSocialSecurityCosts,
        }));
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: {
          ...state.loading,
          socialSecuritySections: false,
        },
      }));
    }
  }, [state.report.legalEntity?.legislation]);

  useEffect(() => {
    state.socialSecurityConfigs.length > 0 && pickRightSocialSecurityCosts(state.socialSecurityConfigs);
  }, [getGrossSalaryAmount, state.report.applicableFrom, pickRightSocialSecurityCosts, state.socialSecurityConfigs]);
  //#endregion

  //#region Populate report with sections
  const populateReportSections = useCallback(() => {
    setState((state) => ({
      ...state,
      report: {
        ...state.report,
        sections: [
          ...state.legislationSections.map(
            (legSection) =>
              ({
                isConstant: false,
                isDeleted: false,
                id: uuid(),
                type: legSection,
                manualRate: null,
                amount: legSection.fixedAmount > 0 ? legSection.fixedAmount : 0,
                systemRate: legSection.rate,
                proformaEmployeeCostReport: state.report,
              } as ProformaEmployeeCostReportSectionRef)
          ),
        ],
      },
    }));
  }, []);
  //Add newly added sections to the report when editing a saved one
  const updateReportWithNewlyAddedSections = useCallback(() => {
    const existingSectionIds = state.report.sections.map((repSection) => repSection.type?.id);

    const newSections = state.legislationSections.filter((legSection) => !existingSectionIds.includes(legSection.id));

    if (newSections.length > 0) {
      setState((state) => ({
        ...state,
        report: {
          ...state.report,
          sections: [
            ...state.report.sections,
            ...newSections.map(
              (legSection) =>
                ({
                  isConstant: false,
                  isDeleted: false,
                  id: uuid(),
                  type: legSection,
                  manualRate: null,
                  amount: 0,
                  systemRate: legSection.rate,
                  proformaEmployeeCostReport: state.report,
                } as ProformaEmployeeCostReportSectionRef)
            ),
          ],
        },
      }));
    }
  }, [state.legislationSections, state.report.sections]);

  //Populate report sections once we load the legislation sections if we are adding a new proforma cost report (effect = add)
  useEffect(() => {
    if (state.legislationSections.length > 0 && effect === "add") {
      populateReportSections();
    } else if (effect === "edit") {
      updateReportWithNewlyAddedSections();
    }
  }, [state.legislationSections, populateReportSections, effect]);
  //#endregion

  //#region Populate legislation social security sections
  const populateReportSocialSecuritySections = useCallback(() => {
    setState((state) => ({
      ...state,
      report: {
        ...state.report,
        sections: [
          ...state.report.sections.filter(
            (legSection: ProformaEmployeeCostReportSectionRef) =>
              !legSection.type?.type?.isSocialSecurityRelevant || legSection.type?.considerIncomeBrackets
          ),
          ...state.report.sections
            .filter(
              (legSection) =>
                legSection.type?.type?.isSocialSecurityRelevant && !legSection.type?.considerIncomeBrackets
            )
            .map((legSection) => {
              const currentSSConfig = state.currentSocialSecurityConfig?.socialSecuritySections?.find((sec) => {
                return sec.section?.id === legSection.type?.id;
              });
              return {
                isConstant: false,
                isDeleted: false,
                id: uuid(),
                type: legSection.type,
                manualRate: null,
                amount:
                  currentSSConfig?.fixedAmount && currentSSConfig?.fixedAmount > 0 ? currentSSConfig?.fixedAmount : 0,
                systemRate: currentSSConfig?.rate,
                proformaEmployeeCostReport: state.report,
              } as ProformaEmployeeCostReportSectionRef;
            }),
        ],
      },
    }));
  }, []);

  useEffect(() => {
    if (!!state.currentSocialSecurityConfig && effect === "add") {
      populateReportSocialSecuritySections();
    }
  }, [
    effect,
    // populateReportSocialSecuritySections,
    state.currentSocialSecurityConfig,
    state.legislationSections,
  ]);
  //#endregion

  //#region Init loading system legislation section (basic + social security sections)
  useEffect(() => {
    if (state.report.legalEntity) {
      loadLegislationSections().then(loadSocialSecuritySectionCosts);
    }
  }, [loadLegislationSections, loadSocialSecuritySectionCosts, state.report.legalEntity]);
  //#endregion

  //#region relod upon config type change
  useEffect(() => {
    if (state.report.legislationEmployeeCostConfigurationType) {
      loadLegislationSections().then(loadSocialSecuritySectionCosts);
    }
  }, [state.report.legislationEmployeeCostConfigurationType]);
  //#endregion

  //#region update section when changing legal entity
  const handleChangeConfirmedLegalEntity = useCallback(() => {
    setState((state) => ({
      ...state,
      fromGrossSalary: null,
      toGrossSalary: null,
      newConfigurationType: undefined,
      report: {
        ...(DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT as ProformaEmployeeCostReportDTO),
        legalEntity: state.newLegalEntity,
        legislationEmployeeCostConfigurationType: null,
        companyAccount: state.newLegalEntity ? (state.newLegalEntity as CompanyLegalEntityRef).companyAccount : null,
      },
    }));
  }, [DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT]);

  //this will update the section when no previous LE was selected
  useEffect(() => {
    if (!!state.newLegalEntity && !state.report.legalEntity) {
      handleChangeConfirmedLegalEntity();
    }
  }, [state.newLegalEntity, state.report.legalEntity, handleChangeConfirmedLegalEntity]);
  //#endregion

  //#region update section when changing legal entity
  const handleChangeConfirmedConfigurationType = useCallback(() => {
    setState((state) => ({
      ...state,
      fromGrossSalary: null,
      toGrossSalary: null,
      newConfigurationType: undefined,
      report: {
        ...(DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT as ProformaEmployeeCostReportDTO),
        legalEntity: state.report.legalEntity,
        legislationEmployeeCostConfigurationType: state.newConfigurationType ?? null,
        companyAccount: state.report.legalEntity?.companyAccount ?? null,
      },
    }));
  }, [DEFAULT_PROFORMA_EMPLOYEE_COST_REPORT]);

  //#endregion

  //#region Save report
  const handleSaveReport = useCallback(async () => {
    setState((state) => ({ ...state, loading: { ...state.loading, saving: true } }));
    try {
      const response = await new ProformaEmployeeCostReportApiService().saveItems([
        {
          ...state.report,
        },
      ]);
      toastifyResponse(response);

      if (response.error) {
        setState((state) => ({
          ...state,
          error: response.error,
        }));
      } else {
        setState((state) => ({
          ...state,
          error: "",
        }));
        handleCloseParent && handleCloseParent();
        handleNotifyParent && handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        error: error,
      }));
    } finally {
      setState((state) => ({ ...state, loading: { ...state.loading, saving: false } }));
    }
  }, [state.report, effect, handleNotifyParent, handleCloseParent]);
  //#endregion

  //#region Export Report
  const handleExportCostReport = useCallback(
    async (paySchema: boolean, costSchema: boolean, sectionsWithZeroAmount: boolean, monthlyView: boolean) => {
      const setLoading = (loading: boolean) => {
        setState((state) => ({ ...state, loading: { ...state.loading, exporting: loading } }));
      };

      await exportReport(
        <ExportedProformaEmployeeCostReport
          report={state.report}
          exportedBlocks={{
            paySchema: paySchema,
            costSchema: costSchema,
            sectionsWithZeroAmount: sectionsWithZeroAmount,
          }}
          monthlyView={monthlyView}
        />,
        setLoading,
        `${state.report.applicableFrom ? intlFormatDateShort(CustomDate.ToDate(state.report.applicableFrom)!) : ""}_${
          state.report.legalEntity?.code
        }_Proforma employee cost report.pdf`
      );
    },
    [state.report]
  );
  //#endregion

  //#region Prehandle export
  const preHandleExport = useCallback(
    async (
      paySchema: boolean,
      costSchema: boolean,
      sectionsWithZeroAmount: boolean,
      saveBefore: boolean,
      monthlyView: boolean
    ) => {
      saveBefore
        ? await handleSaveReport().then(
            () => handleExportCostReport(paySchema, costSchema, sectionsWithZeroAmount, monthlyView),
            (reason) => alert(reason)
          )
        : handleExportCostReport(paySchema, costSchema, sectionsWithZeroAmount, monthlyView);
    },
    [handleExportCostReport, handleSaveReport]
  );
  //#endregion

  //#region save document to be sent to client
  const handleSaveSentDocument = useCallback(
    async (documentId: string) => {
      try {
        if (state.file) {
          const fileBlob = state.file.file;
          const file = {
            file: new File(
              [fileBlob],
              `${
                state.report.applicableFrom ? intlFormatDateShort(CustomDate.ToDate(state.report.applicableFrom)!) : ""
              }_${state.report.legalEntity?.code}_Proforma employee cost report.pdf`
            ),
            fileName: `${
              state.report.applicableFrom ? intlFormatDateShort(CustomDate.ToDate(state.report.applicableFrom)!) : ""
            }_${state.report.legalEntity?.code}_Proforma employee cost report.pdf`,
          };
          const document: DocumentSTO = {
            companyAccount: state.report.companyAccount,
            legalEntity: state.report.legalEntity,
            id: documentId,
            type: DOCUMENT_TYPES.ProformaEmployeeCostReport as DocumentTypeRef,
            category: DOCUMENT_CATEGORIES.EMPLOYMENT_DOCUMENTS as DocumentCategoryRef,
            status: DOCUMENTS_STATUS_TYPES.VERIFIED,
            period: Period.makePeriod(state.report.applicableFrom.year, PeriodType.Month, 1),
            documentDate: CustomDate.ToDate(state.report.applicableFrom) ?? new Date(),
          } as DocumentSTO;

          const documentResponse = await saveDocument(document);

          const invokerId = documentResponse.invokerId;
          const correlationId = documentResponse.correlationId;

          await handleUploadNewFiles(document, [file], invokerId, correlationId);
        }
      } catch (error) {
      } finally {
      }
    },
    [state.report, state.file]
  );
  //#endregion

  //#region save sent report callback
  const handleSaveSentReport = useCallback(
    async (document: DocumentRef) => {
      const itemToSave = {
        proformaEmployeeCostReport: state.report,
        copyToEmails: [...state.users.map((user) => user.email), ...state.externalUsers],
        document: document,
      };
      try {
        const response = await new SendProformaEmployeeCostReportApiService().saveItems([
          { ...itemToSave, id: uuid() },
        ]);
        toastifyResponse(response);
      } catch (error) {}
    },
    [state.externalUsers, state.report, state.users]
  );
  //#endregion

  //#region handle send report
  const handleSendReport = useCallback(async () => {
    //first we save the report
    //then we save the document containing the report
    //then we save sent report
    const documentToSaveId = uuid();

    if (state.file) {
      setState((state) => ({ ...state, loading: { ...state.loading, sending: true } }));
      try {
        await handleSaveReport().then(
          () =>
            handleSaveSentDocument(documentToSaveId).then(
              async () => {
                await handleSaveSentReport({ id: documentToSaveId } as DocumentRef).then(() => {
                  setState((state) => ({
                    ...state,
                    file: null,
                    users: [],
                    externalUsers: [],
                    loading: { ...state.loading, sending: false },
                  }));
                });
              },
              (reason) => alert(reason)
            ),
          (reason) => alert(reason)
        );
      } catch (error) {
        console.log(error);
      }
    }
  }, [state.file, handleSaveReport, handleSaveSentDocument, handleSaveSentReport]);
  //#endregion

  //#region Prehandle send report
  const prehandleSendReport = useCallback(
    async (
      paySchema: boolean,
      costSchema: boolean,
      sectionsWithZeroAmount: boolean,
      users: UserRef[],
      externalUsers: string[],
      monthlyView: boolean
    ) => {
      //Preview file and users
      const file = await generatePDFReport(
        <ExportedProformaEmployeeCostReport
          report={state.report}
          exportedBlocks={{
            paySchema: paySchema,
            costSchema: costSchema,
            sectionsWithZeroAmount: sectionsWithZeroAmount,
          }}
          monthlyView={monthlyView}
        />
      );
      //This will trigger the PreviewProformaEmployeeCostReport so that the user can check the
      //generated proforma and confirm to send (through handleSendReport)
      if (file) {
        const fileBlob = file?.output("blob");
        setState((state) => ({
          ...state,
          file: {
            file: fileBlob,
            fileExtension: "pdf",
            fileName: `${
              state.report.applicableFrom ? intlFormatDateShort(CustomDate.ToDate(state.report.applicableFrom)!) : ""
            }_${state.report.legalEntity?.code}_Proforma employee cots report.pdf `,
          },
          users: users,
          externalUsers: externalUsers,
        }));
      }
    },
    [state.report]
  );
  //#endregion

  //#region updated selected period
  const handleUpdateSelectedPeriod = useCallback((selectedPeriod: SELECTED_PERIOD) => {
    setState((state) => ({
      ...state,
      report: {
        ...state.report,
        selectedPeriod: selectedPeriod,
      },
    }));
  }, []);
  //#endregion

  return (
    <>
      <LoadingOverlay
        error={state.error}
        loading={
          state.loading.legislationSections ||
          state.loading.saving ||
          state.loading.socialSecuritySections ||
          state.loading.exporting
        }
      >
        <ProformaEmployeeCostReportContext.Provider
          value={{
            originalReport: item ?? null,
            report: state.report,
            viewMode: !!state.report.isAcceptedByClient,
            currentSocialSecurityConfig: state.currentSocialSecurityConfig,
            applicableSocialSecurityConfigs: state.applicableSocialSecurityConfigs,
            reportUpdated: state.reportUpdated,
            setReportUpdated: (updated: boolean) => {
              setState((state) => ({ ...state, reportUpdated: updated }));
            },
            grossSalary: getGrossSalaryAmount(),
          }}
        >
          <ReportContainer>
            <Grid style={{ display: "flex", justifyContent: "space-between" }}>
              <EmployeeCostHeader
                handleUpdateGeneralInfo={handleUpdateGeneralInfo}
                handleUpdateLegalEntity={handleUpdateNewLegalEntity}
                handleUpdateConfigurationType={handleUpdateConfigurationType}
              />
              {state.report.legalEntity?.legislation && (
                <Grid>
                  <LegislationSectionConfiguration
                    legislation={state.report.legalEntity.legislation}
                    sections={state.legislationSections}
                    socialSecurityConfig={state.currentSocialSecurityConfig}
                    configurationTypeName={state.report.legislationEmployeeCostConfigurationType?.name}
                  />
                </Grid>
              )}
            </Grid>

            {state.report.legalEntity ? (
              <>
                {state.report.sections.length === 0 ? (
                  state.loading.legislationSections ? (
                    <></>
                  ) : state.legislationSections.length > 0 ? (
                    <Alert severity="info">{`${t(`${tRP}.no_sections`)}`}</Alert>
                  ) : (
                    <Alert severity="warning">
                      {`${t(`${tRP}.no_configured_sections_for_this_legislation`, {
                        legislationName: state.report.legalEntity.legislation?.name,
                      })}`}
                    </Alert>
                  )
                ) : (
                  <Grid
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light, alignSelf: "center" }}
                    >
                      {`${t(`${tRP}.enter_annual_amounts`)}`}.
                    </Typography>
                    <Container>
                      <EmployeeCostSchema
                        handleUpdateSection={handleUpdateSection}
                        handleUpdateSelectedPeriod={handleUpdateSelectedPeriod}
                      />
                      <EmployeePaySchema handleUpdateSection={handleUpdateSection} />
                    </Container>
                    <EmployeeCostActions
                      forceCloseSendDialog={state.loading.sending}
                      handleSaveReport={handleSaveReport}
                      preHandleExport={preHandleExport}
                      prehandleSend={prehandleSendReport}
                      allFieldsValidated={!!state.report.legalEntity && !!state.report.currency}
                    />
                  </Grid>
                )}
              </>
            ) : (
              <Grid style={{ display: "flex", flexDirection: "column" }}>
                <Grid style={{ padding: "5px 16px" }}>
                  <Alert severity="info">{`${t(`${tRP}.select_legal_entity`)}`}</Alert>
                </Grid>
                <DialogActions handleCancel={handleDiscard} />
              </Grid>
            )}
          </ReportContainer>
        </ProformaEmployeeCostReportContext.Provider>
      </LoadingOverlay>

      {!!state.file && (
        <LoadingOverlay loading={state.loading.sending}>
          <PreviewProformaEmployeeCostReport
            file={state.file}
            confirmSending={handleSendReport}
            users={state.users}
            externalUsers={state.externalUsers}
            loading={state.loading.sending}
          />
        </LoadingOverlay>
      )}
      <CustomConfirmationChangesDialog
        title={`${t(`${tRP}.confirm_changing_legal_entity`)}`}
        open={
          (!!state.newLegalEntity &&
            !!state.report.legalEntity &&
            state.newLegalEntity.id !== state.report.legalEntity.id) ||
          (state.newConfigurationType === null && !!state.report.legislationEmployeeCostConfigurationType) ||
          (!!state.newConfigurationType &&
            state.newConfigurationType.id !== state.report.legislationEmployeeCostConfigurationType?.id)
        }
        loading={false}
        handleCancel={() => setState((state) => ({ ...state, newLegalEntity: null, newConfigurationType: null }))}
        actionsAndHandlers={[
          {
            action: "yes",
            handler:
              !!state.newLegalEntity &&
              !!state.report.legalEntity &&
              state.newLegalEntity.id !== state.report.legalEntity.id
                ? handleChangeConfirmedLegalEntity
                : (state.newConfigurationType === null && !!state.report.legislationEmployeeCostConfigurationType) ||
                  (!!state.newConfigurationType &&
                    state.newConfigurationType.id !== state.report.legislationEmployeeCostConfigurationType?.id)
                ? handleChangeConfirmedConfigurationType
                : () => {},
            customButtonStyle: {
              backgroundColor: theme.palette.primary.main,
              textTransform: "none",
              color: theme.palette.primary.contrastText,
            },
          },
        ]}
      >
        <Alert severity="warning">{`${t(`${tRP}.changing_legal_entity_warning`)}`}</Alert>
      </CustomConfirmationChangesDialog>

      <Dialog open={state.sSecurityConfigWarningOpen}>
        <DialogTitle
          title={`${t(`${tRP}.social_security_dialog_warning`)}`}
          handleClose={() => setState((state) => ({ ...state, sSecurityConfigWarningOpen: false }))}
        />
        <DialogContent>
          <Alert severity="warning"> {`${t(`${tRP}.no_social_security_warning`)}`}</Alert>
        </DialogContent>
      </Dialog>
    </>
  );
};
export default ProformaEmployeeCostReport;
